import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { FileText, Save, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = `${API_BASE_URL}/api`;

const BoardNotes = ({ boardId }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!boardId) return;
    setLoading(true);
    axios.get(`${API}/boards/${boardId}/notes`, { withCredentials: true })
      .then(r => { setContent(r.data.content || ''); setUpdatedAt(r.data.updated_at); })
      .catch(() => toast({ title: 'Erreur chargement notes', variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [boardId]);

  const save = useCallback(async (value) => {
    setSaving(true);
    setSaved(false);
    try {
      const r = await axios.put(`${API}/boards/${boardId}/notes`, { content: value }, { withCredentials: true });
      setUpdatedAt(r.data.updated_at);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      toast({ title: 'Erreur sauvegarde', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }, [boardId]);

  const handleChange = (e) => {
    const val = e.target.value;
    setContent(val);
    setSaved(false);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => save(val), 1500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#1C3E53] dark:text-[#4A90A4]" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Notes du board</h2>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
          {saving && <><Loader2 className="w-3 h-3 animate-spin" /> Sauvegarde...</>}
          {saved && !saving && <><CheckCircle2 className="w-3 h-3 text-green-500" /> Sauvegardé</>}
          {!saving && !saved && updatedAt && (
            <span>Modifié {new Date(updatedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 px-6 py-4">
        <textarea
          value={content}
          onChange={handleChange}
          placeholder={`Notes, idées, décisions importantes pour ce board...\n\n• Markdown basique supporté\n• Sauvegarde automatique`}
          className="w-full h-full min-h-[400px] resize-none bg-transparent text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none leading-relaxed font-mono"
          spellCheck={false}
        />
      </div>

      {/* Footer hint */}
      <div className="px-6 py-3 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Sauvegarde automatique · Visible par tous les membres du workspace
        </p>
      </div>
    </div>
  );
};

export default BoardNotes;
